import { type AppApi, AppRequestError } from "@pilotfish/app-api";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { type FamilyArchive, fetchArchive } from "../api/activity";
import { buildArchiveDocument, buildArchiveText, fileName } from "../export/document";
import { copyText, downloadFile } from "../export/download";
import { Loading } from "./states";

/**
 * **Taking a copy** — the panel over `GET /archive` (issue #304, `Export.dc.html`).
 *
 * The thing [ADR 0020](../../../../docs/adr/0020-phase-1-family-data-export.md) could not
 * offer. That ADR had to define *portable* as *"a structured document you can copy"*, in
 * numbered parts sized for a chat window, **because no surface could hand a family a file** —
 * and it named this layer as its re-visit trigger. So: same read, same completeness rules, one
 * file.
 *
 * ## The counts are the completeness claim made checkable
 *
 * That is the canvas's own annotation and it decides the layout. A family can compare these
 * numbers against what the other screens showed them, which is the only way anybody notices an
 * export that quietly left something out. Two consequences follow:
 *
 * - **Every section gets a row**, including ones nobody expects to check. A section counted
 *   nowhere is a section whose absence from the file is invisible, which is the failure the
 *   list exists to catch. A section this build has no phrase for falls back to the server's own
 *   heading rather than disappearing — the memory screen's rule for an unknown `kind`, one
 *   screen over.
 * - **The completeness sentence disappears when `unreadable` is above zero**, and the shortfall
 *   is named in its place. A partial copy that still says *"that's the lot"* is the inventory
 *   lying — the one thing this surface must never do — and it is exactly what `shapeExport`'s
 *   `message` refuses to do for a host.
 *
 * ## What it does not compose
 *
 * The `doesNotInclude` lines are the server's, rendered verbatim, for `Confirm`'s reason one
 * screen over: they say what a family's own record does not contain, and a browser rewording
 * them would be the panel inventing an account of a rule it did not make.
 *
 * That includes the line saying the record is **read a page at a time**, which this surface
 * carries because it is true here too: AP26 loops `Query` on `LastEvaluatedKey` and
 * `ConsistentRead` is per request, so the file is not a single frozen moment (PR #333 review —
 * #304 dropped it on the opposite premise). What the tool states and this does not is the
 * *width* of that window, since its parts are read across turns. Either way the server decides
 * which lines apply; the panel filters none of them.
 *
 * ## Read-only, all the way down
 *
 * No proposal, no confirmation, no `ACTION#`. Reading a family's own record is not a write, so
 * this is the one dialog in the panel with no *Yes, go ahead* in it — and the only one whose
 * primary control is safe to focus on open.
 */
export function TakeACopy({
  api,
  familyId,
  familyName,
  onClose,
}: {
  api: AppApi;
  familyId: string;
  /** From the switcher, which is where the panel's family names come from (`/app/me/families`). */
  familyName: string | null;
  onClose: () => void;
}) {
  const labelId = useId();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [copied, setCopied] = useState<Copied>("idle");
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let live = true;
    fetchArchive(api, familyId)
      .then((archive) => {
        if (live) setLoad({ status: "ready", archive });
      })
      .catch((error: unknown) => {
        if (live) setLoad({ status: "failed", detail: detailOf(error) });
      });
    return () => {
      live = false;
    };
  }, [api, familyId]);

  /**
   * Focus in, and focus back out — `Confirm`'s contract, minus the part about irreversibility.
   *
   * Focus lands on **Close** rather than on *Download*: not because downloading is dangerous,
   * but because the archive is still loading when this opens, so the primary control does not
   * exist yet and focus has to land somewhere that does.
   */
  useEffect(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => {
      const previous = opener.current;
      if (previous !== null && document.contains(previous)) previous.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Tab stays inside — `aria-modal="true"` is otherwise a claim nothing makes true.
   *
   * Kept even though nothing behind this scrim is destructive, because the reason is the same:
   * a modal a keyboard can walk out of is one where the page behind it is live, and this screen
   * has a *Show more* that would silently load pages under a dialog nobody could see them in.
   */
  useEffect(() => {
    const onTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const root = dialogRef.current;
      if (root === null) return;
      const focusable = [...root.querySelectorAll<HTMLElement>("button:not([disabled])")];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onTab);
    return () => window.removeEventListener("keydown", onTab);
  }, []);

  const archive = load.status === "ready" ? load.archive : null;

  const onDownload = useCallback(() => {
    if (archive === null) return;
    downloadFile(
      fileName(familyName, archive.snapshotAt),
      buildArchiveDocument(archive, familyName),
      "text/html;charset=utf-8",
    );
  }, [archive, familyName]);

  const onCopy = useCallback(() => {
    if (archive === null) return;
    // The answer decides the label. A *Copied* over a clipboard that still holds something else
    // is a small lie a family only discovers when they paste — see `copyText`.
    void copyText(buildArchiveText(archive, familyName)).then((ok) =>
      setCopied(ok ? "copied" : "failed"),
    );
  }, [archive, familyName]);

  return (
    <div className="scrim scrim--dialog">
      <div
        className="dialog dialog--copy"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        ref={dialogRef}
      >
        <h2 className="dialog__title" id={labelId}>
          Everything Boone holds about {familyName ?? "your family"}
        </h2>

        {load.status === "loading" ? <Loading label="Gathering your record…" /> : null}

        {load.status === "failed" ? (
          <p className="dialog__error" role="alert">
            {load.detail ??
              "Something went wrong at our end, so we couldn't put your copy together. Try again in a moment — nothing you've told Boone is affected."}
          </p>
        ) : null}

        {archive === null ? null : (
          <>
            <ul className="copy__counts">
              {countRows(archive).map((row) => (
                <li className="copy__count" key={row.key}>
                  <span className="copy__n">{row.count}</span>
                  <span>{row.phrase}</span>
                </li>
              ))}
            </ul>

            {archive.unreadable > 0 ? (
              <p className="copy__short" role="alert">
                {archive.unreadable === 1
                  ? "1 record couldn't be read, so this copy is missing it. Everything else is here."
                  : `${archive.unreadable} records couldn't be read, so this copy is missing them. Everything else is here.`}
              </p>
            ) : (
              <p className="copy__complete">That's the lot; nothing is held back.</p>
            )}

            <div className="copy__actions">
              <button className="cta" type="button" onClick={onDownload}>
                Download the file
              </button>
              <button className="linklike" type="button" onClick={onCopy}>
                or copy it as text
              </button>
              {/* `aria-live` rather than `role="alert"`: this is the outcome of something the
                  family just pressed, not an interruption, and both outcomes read the same way. */}
              <span className="copy__copied" aria-live="polite">
                {copied === "copied" ? "Copied" : copied === "failed" ? "Couldn't copy" : ""}
              </span>
            </div>

            <p className="copy__note">
              It opens in your browser, prints like a document, and carries the same record as data
              inside it.
            </p>

            <div className="copy__not">
              <h3 className="copy__not-title">What it says it is not</h3>
              <ul className="dialog__list">
                {archive.doesNotInclude.map((line) => (
                  <li className="dialog__list-item" key={line}>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <div className="dialog__actions">
          <button className="cta cta--quiet" type="button" onClick={onClose} ref={closeRef}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

type Load =
  | { status: "loading" }
  | { status: "failed"; detail: string | null }
  | { status: "ready"; archive: FamilyArchive };

type Copied = "idle" | "copied" | "failed";

/**
 * What each section is called in the counts list — `Export.dc.html`'s phrasing.
 *
 * **Panel copy rather than the server's**, and that is the one place on this screen it is. The
 * section labels the server sends (*"Everything on your list"*, *"The record of what changed"*)
 * are headings for a document; these are the tail of a sentence that starts with a number, and
 * they say what a family would check them against. The server's own words are what the **file**
 * prints, which is the document those headings were written for.
 *
 * Keyed on the wire name and never on the label, so re-wording a heading cannot silently drop a
 * row into {@link fallbackPhrase}.
 */
const SECTION_PHRASES: Readonly<Record<string, { one: string; many: string }>> = {
  family: {
    one: "family — its name, and how you like Boone to work",
    many: "family — its name, and how you like Boone to work",
  },
  members: { one: "person", many: "people" },
  invitations: { one: "invitation", many: "invitations" },
  backlog: {
    one: "thing on your list, open or finished",
    many: "things on your list, open and finished",
  },
  memory: {
    one: "thing Boone remembers about you, in your own words",
    many: "things Boone remembers about you, in your own words",
  },
  pending: { one: "thing waiting for your yes", many: "things waiting for your yes" },
  notifications: { one: "email sent on your behalf", many: "emails sent on your behalf" },
  feedback: { one: "report you sent us", many: "reports you sent us" },
  history: { one: "change, including the deletions", many: "changes, including the deletions" },
};

/**
 * A section this build has no phrase for.
 *
 * The memory screen's rule for an unknown `kind`, and it matters more here: dropping the row
 * would leave a whole section of the file counted nowhere, on the list whose entire job is to
 * let a family notice something missing.
 */
function fallbackPhrase(label: string): string {
  return label.toLowerCase();
}

interface CountRow {
  readonly key: string;
  readonly count: number;
  readonly phrase: string;
}

/**
 * One row per section that has anything in it.
 *
 * **Sections with nothing are left out rather than shown as zero**, which is the same call
 * `shapeInventory` and the archive itself make: the file has no empty sections either, so a
 * zero row would be counting something that is not in the document. What a family checks is
 * that the numbers here match what they saw elsewhere, and a family with no invitations has
 * nothing to check against.
 */
function countRows(archive: FamilyArchive): readonly CountRow[] {
  return archive.sections.map(({ section, label, entries }) => {
    const phrase = SECTION_PHRASES[section];
    return {
      key: section,
      count: entries.length,
      phrase:
        phrase === undefined
          ? fallbackPhrase(label)
          : entries.length === 1
            ? phrase.one
            : phrase.many,
    };
  });
}

/** The server's own sentence for a refusal, or `null` to fall back to a generic one. */
function detailOf(error: unknown): string | null {
  return error instanceof AppRequestError ? error.detail : null;
}
